import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../api/auth';
import { devLog } from '../utils/devLog';

// SECURE ADMIN ROUTE - Randomized path for security
// This makes it harder for attackers to find the admin panel
export const ADMIN_ROUTE_PREFIX = '/system-control-panel-7x9k2m';

interface AdminRouteProps {
  children: React.ReactElement;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const location = useLocation();
  const [authState, setAuthState] = useState<{
    checked: boolean;
    isAuthenticated: boolean;
    isAdmin: boolean;
    error: string | null;
  }>({
    checked: false,
    isAuthenticated: false,
    isAdmin: false,
    error: null,
  });

  useEffect(() => {
    const verifyAdminAccess = async () => {
      // Small delay to ensure token is saved
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        devLog('[AdminRoute] Starting admin access verification');

        // Step 1: Check if admin token exists
        const token = localStorage.getItem('adminToken');
        devLog('[AdminRoute] Admin token present:', !!token);

        if (!token) {
          devLog('[AdminRoute] No admin token');
          setAuthState({
            checked: true,
            isAuthenticated: false,
            isAdmin: false,
            error: 'No authentication token',
          });
          return;
        }

        // Step 2: Verify session with backend
        const user = await authService.verifyAdminSession();
        devLog('[AdminRoute] Verify response user:', user);

        if (!user) {
          devLog('[AdminRoute] Verify returned no user');
          setAuthState({
            checked: true,
            isAuthenticated: false,
            isAdmin: false,
            error: 'Invalid session',
          });
          return;
        }

        // Step 3: Check admin role
        devLog('[AdminRoute] Role:', user.role);
        if (user.role !== 'admin') {
          // Log unauthorized access attempt
          console.warn(`Unauthorized admin access attempt by user: ${user.email}`);
          setAuthState({
            checked: true,
            isAuthenticated: true,
            isAdmin: false,
            error: 'Admin privileges required',
          });
          return;
        }

        // Step 4: All checks passed
        devLog('[AdminRoute] Access OK (admin)');
        setAuthState({
          checked: true,
          isAuthenticated: true,
          isAdmin: true,
          error: null,
        });

      } catch (err: any) {
        console.error('Admin route verification error:', err);
        setAuthState({
          checked: true,
          isAuthenticated: false,
          isAdmin: false,
          error: err.message || 'Authentication failed',
        });
      }
    };

    verifyAdminAccess();
  }, []);

  // Show loading spinner while checking
  if (!authState.checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to admin login page
  if (!authState.isAuthenticated) {
    devLog('[AdminRoute] Not authenticated → admin login');
    return <Navigate to="/system-access-9x4m2k" replace state={{ from: location }} />;
  }

  // Authenticated but not admin - redirect to homepage with message
  if (!authState.isAdmin) {
    console.warn('[AdminRoute] User authenticated but not admin, redirecting to homepage');
    return (
      <Navigate 
        to="/" 
        replace 
        state={{ 
          error: 'Access denied: Admin privileges required',
          showToast: true 
        }} 
      />
    );
  }

  // All checks passed - render admin content
  return children;
};

export default AdminRoute;
